/**
 * InsightsPage — tela Insights, fiel ao protótipo insights.html.
 *
 * Estrutura (alinhada ao protótipo aprovado B.50):
 *   1. Page Header editorial (eyebrow + título em 2 linhas + sub + meta)
 *   2. Funil KPI — kpi-card span-12 hero-primary com CROSS Bar Chart
 *      - FLAG_FUNIL_REAL=false → placeholder honesto (gap backend: /insights/kpis inexistente)
 *   3. Toolbar de refresh+filtro — integrada discretamente acima da faixa editorial
 *   4. Editorial Intro Band — faixa roxa com manchete + lista de oportunidades (opp-list)
 *      - Usa dados reais da API /insights
 *   5. Divisor "Descobertas" + Feed de Insight Cards — sistema editorial kicker+manchete
 *      - Dados reais da API /insights (já existente)
 *      - Bloco ins-ic-nums: "—" honesto (gap backend: API não retorna numeros[])
 *   6. FAB "Perguntar à CROSS" → /chat
 *   7. Modal "Origem do dado" — InsightsSourceModal
 *
 * FLAGS de gap backend:
 *   - FLAG_FUNIL_REAL=false  → /insights/kpis inexistente (F2 backlog)
 *   - ins-ic-nums exibe "—"  → /insights não retorna numeros[] estruturados
 *   - topbar freshness chip  → sem lastSync no AppLayout shell (gap documentado lá)
 *
 * Regras:
 *   - Zero string hardcoded fora de i18n
 *   - Zero acesso direto a env vars
 *   - A11y AA: aria-labels, focus, keyboard nav, roles semânticos
 *   - CSS: screen-insights.css exclusivo (nunca edita global.css)
 */

import '../styles/screen-insights.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { Skeleton } from '../components/Skeleton';
import { InsightsBarChart, type InsightsBarChartItem } from '../components/InsightsBarChart';
import {
  InsightsSourceModal,
  type InsightsSourceModalData,
  type SourceKey,
} from '../components/InsightsSourceModal';
import { apiClient, ApiError } from '../lib/api-client';

/* ================================================================
   TIPOS
   ================================================================ */

interface Insight {
  readonly id: string;
  readonly category: string;
  readonly summary: string;
  readonly details: string;
  readonly suggestedAction: string;
  readonly createdAt: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'ready'; data: readonly Insight[] }
  | { kind: 'error' };

type RefreshState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'success' }
  | { kind: 'no-data' }
  | { kind: 'rate-limited' }
  | { kind: 'error' };

interface RefreshResponse {
  readonly status: 'generated' | 'skipped';
  readonly report_id?: string;
  readonly reason?: string;
}

/* ================================================================
   MAPA DE FONTES — determina dot color e label de plataforma
   Heurística: verifica category/summary do insight
   ================================================================ */

function guessSourceKey(insight: Insight): SourceKey {
  const cat = (insight.category ?? '').toLowerCase();
  const sum = (insight.summary ?? '').toLowerCase();
  if (cat.includes('ads') || sum.includes('ads') || cat.includes('meta') || sum.includes('meta')) {
    return 'ads';
  }
  if (
    cat.includes('drive') ||
    cat.includes('planilha') ||
    sum.includes('planilha') ||
    sum.includes('drive')
  ) {
    return 'drive';
  }
  return 'ig';
}

const SOURCE_DOT_CLASS: Record<SourceKey, string> = {
  ig: 'ins-dot-ig',
  ads: 'ins-dot-ads',
  drive: 'ins-dot-drive',
};

function getSourceLabel(key: SourceKey): string {
  if (key === 'ig') return t('insights.sourceInstagram');
  if (key === 'ads') return t('insights.sourceMetaAds');
  return t('insights.sourceDrive');
}

/* ================================================================
   UTILIDADES
   ================================================================ */

function formatDatePtBr(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function addRipple(el: HTMLElement, event: React.MouseEvent): void {
  const rect = el.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  const size = Math.max(rect.width, rect.height) * 1.2;
  const ripple = document.createElement('div');
  ripple.className = 'ins-ripple';
  ripple.style.cssText = `width:${size}px;height:${size}px;left:${x - size / 2}px;top:${y - size / 2}px`;
  el.appendChild(ripple);
  ripple.addEventListener('animationend', () => ripple.remove());
}

/* ================================================================
   FUNIL KPI — dados mock honesto
   FLAG (gap backend): o endpoint /insights/kpis não existe em F2.
   Quando o endpoint existir, substituir FUNIL_STATIC por dados reais.
   Os valores são representativos (escala 0–100) baseados nos dados
   do cliente piloto (Casa de Bolos) — não devem ser exibidos como
   números brutos sem o endpoint real.
   ================================================================ */

/**
 * FLAG_FUNIL_REAL: quando false, o funil mostra placeholder honesto.
 * Mudar para true e apontar para dados reais quando /insights/kpis existir.
 */
const FLAG_FUNIL_REAL = false;

const FUNIL_STATIC: readonly InsightsBarChartItem[] = [
  { label: 'Alcance', value: 92, highlight: true },
  { label: 'Salvamentos', value: 78 },
  { label: 'Faturamento', value: 72 },
  { label: 'Engajamento', value: 54 },
  { label: 'CPC', value: 44 },
];

/* ================================================================
   CARD DE INSIGHT — componente interno
   ================================================================ */

interface InsightCardProps {
  readonly insight: Insight;
  readonly index: number;
  readonly isAlert: boolean;
  readonly isNew: boolean;
  readonly manchete: string;
  readonly onSourceClick: (data: InsightsSourceModalData) => void;
  readonly onChatClick: (insightId: string) => void;
}

function InsightCard({
  insight,
  index,
  isAlert,
  isNew,
  manchete,
  onSourceClick,
  onChatClick,
}: InsightCardProps): JSX.Element {
  const cardRef = useRef<HTMLElement>(null);
  const srcKey = guessSourceKey(insight);
  const delay = 60 + index * 70;

  const handleClick = (e: React.MouseEvent<HTMLElement>): void => {
    if (cardRef.current !== null) addRipple(cardRef.current, e);
  };

  const handleSourceChipClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onSourceClick({
      insightTitle: insight.summary,
      primaryValue: '—',
      sourceKey: srcKey,
      accountLabel: '—',
      periodLabel: formatDatePtBr(insight.createdAt),
      collectedLabel: formatDatePtBr(insight.createdAt),
      narrativa: insight.details,
      insightId: insight.id,
    });
  };

  const handleChatBtnClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onChatClick(insight.id);
  };

  const kickerText = isAlert ? t('insights.kickerAttention') : t('insights.kickerDiscovery');
  const typeIcon = isAlert ? 'warning' : 'lightbulb';
  const kickerClass = isAlert ? 'ins-kicker ins-kicker--alert' : 'ins-kicker';

  return (
    <article
      ref={cardRef}
      className={`ins-ic-card${isAlert ? ' ins-ic-card--alert' : ''}`}
      style={{ animationDelay: `${delay}ms` }}
      aria-label={t('insights.insightAriaLabel', { title: manchete })}
      onClick={handleClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ')
          handleClick(e as unknown as React.MouseEvent<HTMLElement>);
      }}
    >
      {/* Topo: kicker + ícone de tipo */}
      <div className="ins-ic-top-line">
        <div className={kickerClass} aria-label={kickerText}>
          {kickerText}
          {isNew ? (
            <span className="ins-ic-badge-novo" aria-label={t('insights.badgeNew')}>
              {t('insights.badgeNew')}
            </span>
          ) : null}
        </div>
        <span
          className="ins-ic-type-icon material-symbols-rounded"
          aria-hidden="true"
          style={{ fontVariationSettings: "'FILL' 0,'wght' 300,'GRAD' 0,'opsz' 20" }}
        >
          {typeIcon}
        </span>
      </div>

      {/* Manchete editorial */}
      <h2 className="ins-headline ins-headline--card">{manchete}</h2>

      {/* Narrativa: usa summary como narrativa principal, details como suporte */}
      <p className="ins-ic-narrativa">{insight.summary}</p>

      {/* Bloco de números — FLAG (gap backend): API /insights não retorna numeros[] estruturados.
          Protótipo exibe valores reais (ex.: "1.274.054", "+12%"). Aqui: "—" honesto.
          Manter este bloco para preservar o layout do card; chip de fonte é clicável.
          Quando backend retornar numeros[], substituir "—" pelos valores reais. */}
      <div className="ins-ic-nums">
        <div className="ins-ic-num-block">
          <span
            className={`ins-ic-num ins-ic-num--flag${isAlert ? ' ins-ic-num--alert-flag' : ''}`}
            aria-label={t('insights.numsFlag')}
          >
            —
          </span>
          <span className="ins-ic-num-label">{t('insights.numsFlag')}</span>
          <button
            type="button"
            className="ins-ic-source-chip"
            onClick={handleSourceChipClick}
            aria-label={t('insights.sourceOriginLabel', { source: getSourceLabel(srcKey) })}
          >
            <span className={`ins-src-dot ${SOURCE_DOT_CLASS[srcKey]}`} aria-hidden="true" />
            {t('insights.sourceVia', { source: getSourceLabel(srcKey) })}
          </button>
        </div>
      </div>

      {/* Ações — botão primário "Ver métricas" (bar_chart) + ghost "Conversar com a CROSS" */}
      {/* Alinhado ao protótipo: ic-btn-primary com bar_chart + ic-btn-ghost com forum */}
      <div className="ins-ic-actions">
        <button
          type="button"
          className="ins-ic-btn-primary"
          onClick={handleChatBtnClick}
          aria-label={t('insights.btnMetrics')}
        >
          <span
            className="material-symbols-rounded"
            aria-hidden="true"
            style={{ fontVariationSettings: "'FILL' 0,'wght' 500,'GRAD' 0,'opsz' 20" }}
          >
            bar_chart
          </span>
          {t('insights.btnMetrics')}
        </button>

        <button
          type="button"
          className="ins-ic-btn-ghost"
          onClick={(e) => {
            e.stopPropagation();
            onChatClick(insight.id);
          }}
          aria-label={t('insights.btnChatCross')}
        >
          <span
            className="material-symbols-rounded"
            aria-hidden="true"
            style={{ fontVariationSettings: "'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 20" }}
          >
            forum
          </span>
          {t('insights.btnChatCross')}
        </button>
      </div>
    </article>
  );
}

/* ================================================================
   LISTA DE OPORTUNIDADES na faixa editorial
   ================================================================ */

interface OppListProps {
  readonly insights: readonly Insight[];
  readonly onOppClick: (insight: Insight) => void;
}

function OppList({ insights, onOppClick }: OppListProps): JSX.Element {
  return (
    <ul className="ins-opp-list" aria-label={t('insights.editorialHeadline')}>
      {insights.map((ins) => {
        const isAlert =
          ins.category.toLowerCase().includes('alert') ||
          ins.category.toLowerCase().includes('atencao') ||
          ins.category.toLowerCase().includes('atençã');
        const shortTitle = ins.summary.length > 60 ? ins.summary.slice(0, 57) + '...' : ins.summary;
        const srcKey = guessSourceKey(ins);

        return (
          <li key={ins.id}>
            <button
              type="button"
              className={`ins-opp-btn${isAlert ? ' ins-opp-btn--alert' : ''}`}
              onClick={() => onOppClick(ins)}
              aria-label={t('insights.oppItemAriaLabel', { title: shortTitle })}
            >
              <span
                className={`ins-opp-icon material-symbols-rounded${isAlert ? ' ins-opp-icon--alert' : ''}`}
                aria-hidden="true"
              >
                {isAlert ? 'warning' : 'lightbulb'}
              </span>
              <span className="ins-opp-text">
                <span className="ins-opp-title">{shortTitle}</span>
                <span className="ins-opp-micro">
                  {getSourceLabel(srcKey)} · {formatDatePtBr(ins.createdAt)}
                </span>
              </span>
              <span className="ins-opp-chevron material-symbols-rounded" aria-hidden="true">
                chevron_right
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/* ================================================================
   InsightsPage — componente principal
   ================================================================ */

export function InsightsPage(): JSX.Element {
  /* estado de dados */
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [refresh, setRefresh] = useState<RefreshState>({ kind: 'idle' });
  const [category, setCategory] = useState<string>('all');

  /* estado do modal */
  const [modalData, setModalData] = useState<InsightsSourceModalData | null>(null);

  const navigate = useNavigate();

  /* ----- load insights ----- */
  const loadInsights = useCallback(async (signal?: { cancelled: boolean }): Promise<void> => {
    try {
      const data = await apiClient.get<readonly Insight[]>('/insights');
      if (signal?.cancelled) return;
      if (data.length === 0) {
        setState({ kind: 'empty' });
        return;
      }
      setState({ kind: 'ready', data });
    } catch {
      if (!signal?.cancelled) setState({ kind: 'error' });
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    void loadInsights(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadInsights]);

  /* ----- refresh ----- */
  const handleRefresh = useCallback(async (): Promise<void> => {
    setRefresh({ kind: 'running' });
    try {
      const res = await apiClient.post<RefreshResponse>('/insights/refresh');
      if (res.status === 'skipped') {
        setRefresh({ kind: 'no-data' });
        return;
      }
      await loadInsights();
      setRefresh({ kind: 'success' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setRefresh({ kind: 'rate-limited' });
        return;
      }
      setRefresh({ kind: 'error' });
    }
  }, [loadInsights]);

  /* ----- categorias disponíveis ----- */
  const categories = useMemo<readonly string[]>(() => {
    if (state.kind !== 'ready') return [];
    const set = new Set<string>();
    for (const i of state.data) set.add(i.category);
    return Array.from(set);
  }, [state]);

  /* ----- filtro ----- */
  const filtered: readonly Insight[] =
    state.kind === 'ready'
      ? category === 'all'
        ? state.data
        : state.data.filter((i) => i.category === category)
      : [];

  /* ----- navegação para chat ----- */
  const openChat = (insightId: string): void => {
    navigate(`/chat?context=insight:${insightId}`);
  };

  /* ----- opp click: abre modal ----- */
  const handleOppClick = (insight: Insight): void => {
    const srcKey = guessSourceKey(insight);
    setModalData({
      insightTitle: insight.summary,
      primaryValue: '—',
      sourceKey: srcKey,
      accountLabel: '—',
      periodLabel: formatDatePtBr(insight.createdAt),
      collectedLabel: formatDatePtBr(insight.createdAt),
      narrativa: insight.details,
      insightId: insight.id,
    });
  };

  /* ----- manchetes editoriais: usa summary real ----- */
  const getManchete = (insight: Insight): string => insight.summary;

  const isAlertInsight = (insight: Insight): boolean => {
    const cat = insight.category.toLowerCase();
    return (
      cat.includes('alert') ||
      cat.includes('atencao') ||
      cat.includes('atençã') ||
      cat.includes('risco') ||
      cat.includes('warning')
    );
  };

  const refreshDisabled = refresh.kind === 'running';

  /* ================================================================
     RENDER
     ================================================================ */

  return (
    /* padding-bottom garante que o FAB fixo não sobreponha o último card */
    <section aria-labelledby="insights-page-title" className="ins-page-root">
      {/* ---- PAGE HEADER EDITORIAL ---- */}
      <div className="ins-page-header">
        <div>
          <p className="ins-page-eyebrow">{t('insights.pageEyebrow')}</p>
          <h1 id="insights-page-title" className="ins-page-title">
            {t('insights.pageTitle')}
            <br />
            <em>{t('insights.pageTitleEm')}</em>
          </h1>
          <p className="ins-page-sub">{t('insights.pageSub')}</p>
        </div>
        <div className="ins-page-meta">
          <span>{t('insights.pageMetaLabel')}: </span>
          <strong>{t('insights.pageMetaSources')}</strong>
        </div>
      </div>

      {/* ---- FUNIL KPI ---- */}
      {/*
       * Estrutura alinhada ao protótipo: kpi-card span-12 hero-primary
       * FLAG (gap backend — F2 backlog):
       * O endpoint /insights/kpis não existe ainda.
       * FLAG_FUNIL_REAL = false → placeholder honesto sem inventar números.
       * Quando o endpoint existir:
       *   1. Mudar FLAG_FUNIL_REAL para true
       *   2. Buscar dados reais e mapear para InsightsBarChartItem[]
       */}
      <section className="ins-funil-section" aria-label={t('insights.funilAriaLabel')}>
        <div className="ins-funil-card ins-kpi-card-hero">
          <p className="ins-funil-label">{t('insights.funilLabel')}</p>
          {FLAG_FUNIL_REAL ? (
            <div className="ins-cbc-wrap" style={{ marginTop: '16px', position: 'relative' }}>
              <InsightsBarChart
                data={FUNIL_STATIC}
                ariaLabel={t('insights.funilAriaLabel')}
                animDelay={300}
                stagger={60}
              />
            </div>
          ) : (
            <div className="ins-funil-empty-flag">
              <span className="material-symbols-rounded" aria-hidden="true">
                info
              </span>
              <span>{t('insights.funilEmptyFlag')}</span>
            </div>
          )}
        </div>
      </section>

      {/* ---- TOOLBAR DISCRETA: refresh + filtro ----
           Funcionalidade real sem contrapartida no protótipo.
           Renderizada de forma discreta antes do bloco de dados prontos.
           Visível apenas quando há dados carregados ou feedback de ação em andamento.
           Não exibida durante loading/empty/error para não poluir esses estados.
      ---- */}
      {state.kind === 'ready' ||
      refresh.kind === 'running' ||
      refresh.kind === 'success' ||
      refresh.kind === 'no-data' ||
      refresh.kind === 'rate-limited' ||
      refresh.kind === 'error' ? (
        <div className="ins-toolbar-micro" role="toolbar" aria-label={t('insights.refreshCta')}>
          <button
            type="button"
            className="ins-refresh-micro"
            onClick={() => void handleRefresh()}
            disabled={refreshDisabled}
            aria-busy={refreshDisabled}
            title={refresh.kind === 'running' ? t('insights.refreshing') : t('insights.refreshCta')}
          >
            <span
              className="material-symbols-rounded"
              aria-hidden="true"
              style={{
                fontSize: '14px',
                fontVariationSettings: "'FILL' 0,'wght' 500,'GRAD' 0,'opsz' 20",
              }}
            >
              {refresh.kind === 'running' ? 'hourglass_top' : 'refresh'}
            </span>
            {refresh.kind === 'running' ? t('insights.refreshing') : t('insights.refreshCta')}
          </button>

          {refresh.kind === 'success' ? (
            <span role="status" className="ins-status-msg ins-status-success">
              {t('insights.refreshSuccess')}
            </span>
          ) : null}
          {refresh.kind === 'no-data' ? (
            <span role="status" className="ins-status-msg ins-status-warn">
              {t('insights.refreshNoData')}
            </span>
          ) : null}
          {refresh.kind === 'rate-limited' ? (
            <span role="alert" className="ins-status-msg ins-status-error">
              {t('insights.refreshRateLimited')}
            </span>
          ) : null}
          {refresh.kind === 'error' ? (
            <span role="alert" className="ins-status-msg ins-status-error">
              {t('insights.refreshError')}
            </span>
          ) : null}

          {/* Filtro de categoria — só aparece quando há >1 categoria */}
          {state.kind === 'ready' && categories.length > 1 ? (
            <div className="ins-filter-micro">
              <label htmlFor="insights-filter" className="ins-filter-label">
                {t('insights.filter')}
              </label>
              <select
                id="insights-filter"
                className="ins-filter-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="all">{t('insights.filterAll')}</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ---- LOADING ---- */}
      {state.kind === 'loading' ? (
        <div
          className="ins-feed-skeleton"
          role="status"
          aria-label={t('insights.loading')}
          aria-busy="true"
        >
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="ins-ic-card-skeleton" aria-hidden="true">
              <Skeleton variant="text" width="20%" />
              <Skeleton variant="heading" width="70%" />
              <Skeleton variant="text" width="90%" />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="40%" />
            </div>
          ))}
        </div>
      ) : null}

      {/* ---- ERROR ---- */}
      {state.kind === 'error' ? (
        <div role="alert" style={{ padding: 'var(--mkv-space-6)', color: 'var(--md-error)' }}>
          <p>{t('insights.error')}</p>
        </div>
      ) : null}

      {/* ---- EMPTY ---- */}
      {state.kind === 'empty' ? (
        <section
          className="empty-state"
          aria-live="polite"
          style={{
            textAlign: 'center',
            padding: 'var(--mkv-space-12) var(--mkv-space-6)',
            color: 'var(--md-on-surface-variant)',
          }}
        >
          <span
            className="material-symbols-rounded"
            aria-hidden="true"
            style={{
              fontSize: '48px',
              color: 'var(--md-outline)',
              display: 'block',
              marginBottom: 'var(--mkv-space-4)',
            }}
          >
            auto_awesome
          </span>
          <p style={{ maxWidth: '480px', margin: '0 auto var(--mkv-space-4)' }}>
            {t('insights.empty')}
          </p>
          <Link
            to="/sources"
            style={{
              color: 'var(--md-primary)',
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            {t('insights.emptyCta')}
          </Link>
        </section>
      ) : null}

      {/* ---- DADOS PRONTOS ---- */}
      {state.kind === 'ready' ? (
        <>
          {/* Faixa editorial intro — fundo roxo, lista de oportunidades.
              Alinhada ao protótipo: feed-editorial-intro com kicker "Esta semana",
              manchete com contagem de insights, guia, opp-list clicável. */}
          <div
            className="ins-editorial-intro"
            role="region"
            aria-label={t('insights.editorialHeadline')}
          >
            <div className="ins-editorial-intro-inner">
              <div className="ins-kicker ins-kicker--inverted">{t('insights.editorialKicker')}</div>
              <h2 className="ins-headline ins-headline--editorial">
                {state.data.length === 1
                  ? t('insights.editorialHeadlineSingle')
                  : t('insights.editorialHeadlinePlural', { n: String(state.data.length) })}
              </h2>
              <p className="ins-guide">{t('insights.editorialGuide')}</p>
              <OppList insights={state.data.slice(0, 5)} onOppClick={handleOppClick} />
            </div>
          </div>

          {/* Divisor: Descobertas — filete tricolor + kicker CAIXA-ALTA */}
          <div className="ins-section-divider" style={{ animationDelay: '80ms' }}>
            <div className="ins-kicker" style={{ marginBottom: 0 }}>
              {t('insights.sectionDiscoveries')}
            </div>
            <div className="ins-section-divider-line" aria-hidden="true" />
          </div>

          {/* Feed de insight cards */}
          <div className="ins-feed" aria-label={t('insights.editorialHeadline')}>
            {filtered.map((insight, i) => {
              const isAlert = isAlertInsight(insight);

              /* Divisor "Merece atenção" antes do primeiro alerta */
              const prevInsight = i > 0 ? filtered[i - 1] : undefined;
              const prevIsAlert = prevInsight !== undefined && isAlertInsight(prevInsight);
              const isFirstAlert =
                isAlert && (i === 0 || (prevInsight !== undefined && !isAlertInsight(prevInsight)));

              return (
                <div key={insight.id}>
                  {isFirstAlert && !prevIsAlert ? (
                    <div
                      className="ins-section-divider"
                      style={{ animationDelay: `${40 + i * 70 - 20}ms` }}
                    >
                      <div className="ins-kicker ins-kicker--alert" style={{ marginBottom: 0 }}>
                        {t('insights.sectionAttention')}
                      </div>
                      <div className="ins-section-divider-line" aria-hidden="true" />
                    </div>
                  ) : null}

                  <InsightCard
                    insight={insight}
                    index={i}
                    isAlert={isAlert}
                    isNew={false}
                    manchete={getManchete(insight)}
                    onSourceClick={setModalData}
                    onChatClick={openChat}
                  />
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {/* ---- FAB "Perguntar à CROSS" ---- */}
      <button
        type="button"
        className="ins-fab"
        onClick={() => navigate('/chat')}
        aria-label={t('insights.fabLabel')}
      >
        <span
          className="ins-fab-icon material-symbols-rounded"
          aria-hidden="true"
          style={{ fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 24" }}
        >
          forum
        </span>
        <span className="ins-fab-label">{t('insights.fabLabel')}</span>
      </button>

      {/* ---- MODAL Origem do dado ---- */}
      <InsightsSourceModal data={modalData} onClose={() => setModalData(null)} />
    </section>
  );
}
